import { LOADING_UI, UPLOADING, SET_ERRORS } from "../types";
import { db } from "../../firebase/firebase";
import store from "../store";
import { mapKV } from "../../utils/map";
import logAnalyticsEvent from "../../utils/logAnalyticsEvent";
import { uploadFile, Uploader } from "./utils";
import { getIconStoragePath, getOrganizationRef, getCurrentUserOrganizationRef, getStagingStoragePath, toGSBucketPath, getApkStoragePath, getObbStoragePath } from "./utils";
import { MANAGED_APPS, createInfoUrl } from "../../utils/routes";
import delay from 'await-delay'

export const addNewExternalApp = (title, packageName, description, categories, icon, organizationKey, globalAccess = false) => dispatch => {
    dispatch({ type: LOADING_UI, payload: true });
    dispatch({
        type: UPLOADING,
        payload: { status: true, title: "New External App" }
    });
    var promises = [];
    promises.push(uploadFile(getIconStoragePath(packageName), icon, dispatch));
    Promise.all(promises).then(urls => {
        dispatch({
            type: UPLOADING,
            payload: { status: false }
        });
        const iconUrl = urls[0];
        db.collection("apps").add({
            title,
            packageName,
            description,
            categories,
            iconUrl,
            showInLaunchers: true,
            vrTypes: ["UNKNOWN"],
            latestVersion: 0,
            organizationRef: getOrganizationRef(organizationKey),
            globalAccess
        });
        logAnalyticsEvent("add_external_app", { packageName, title });
    });
};
export const editExternalApp = (key, title, packageName, description = "", categories, icon, oldIconUrl, organizationKey, globalAccess, sortPriority) => dispatch => {
    dispatch({ type: LOADING_UI, payload: true });
    dispatch({
        type: UPLOADING,
        payload: { status: true, title: "External App: " + title }
    });
    var promises = [];
    if (icon)
        promises.push(uploadFile(getIconStoragePath(packageName), icon, dispatch));
    Promise.all(promises).then(urls => {
        dispatch({
            type: UPLOADING,
            payload: { status: false }
        });
        const iconUrl = urls[0] ? urls[0] : oldIconUrl;
        db.collection("apps")
            .doc(key)
            .update({
                title,
                packageName,
                description,
                categories,
                iconUrl,
                organizationRef: getOrganizationRef(organizationKey),
                globalAccess,
                sortPriority
            });
    });
};

const DEFAULT_ICON_PATH = "https://firebasestorage.googleapis.com/v0/b/mighty-platform-prod.appspot.com/o/public%2FdefaultIcon.png?alt=media&token=6d588e59-0ec4-4ffc-bf16-0764cd70f89a";
export const addNewManagedApp = (apkFile, obbFile, newAppData, history) => async (dispatch) => {
    dispatch({ type: LOADING_UI, payload: true });

    dispatch({
        type: UPLOADING,
        payload: { status: true, title: `Uploading Managed App` }
    });

    let uploadFiles = [];
    let apkStoragePath = getApkStoragePath(newAppData);
    uploadFiles.push({ file: apkFile, storagePath: apkStoragePath });

    let obbStoragePath;
    if (obbFile) {
        obbStoragePath = getObbStoragePath(newAppData);
        uploadFiles.push({ file: obbFile, storagePath: obbStoragePath });
    }

    try {
        let uploader = new Uploader(dispatch);
        let snapshots = await uploader.uploadFiles(uploadFiles);

        let success = true;
        snapshots.forEach(snapshot => success = success && snapshot.state === "success")
        if (!success) {
            throw new Error("Error creating new App. Please try again.");
        };

        newAppData = {
            packageName: newAppData.packageName,
            latestVersion: newAppData.latestVersion,
            versionName: newAppData.versionName,
            title: newAppData.title,
            apkUrl: toGSBucketPath(apkStoragePath),
            obbUrls: obbStoragePath ? [toGSBucketPath(obbStoragePath)] : [],
            organizationRef: getCurrentUserOrganizationRef(),
            obbNames: [],
            iconUrl: newAppData.iconUrl || DEFAULT_ICON_PATH,
            showInLaunchers: newAppData.showInLaunchers === undefined ? true : newAppData.showInLaunchers,
            vrTypes: newAppData.vrTypes || ["UNKNOWN"],
            categories: newAppData.categories || [],
            globalAccess: newAppData.globalAccess === undefined ? false : newAppData.globalAccess,
            sortPriority: newAppData.sortPriority || 0,
            description: newAppData.description || '',
            defaultPlatformAdminApp: newAppData.defaultPlatformAdminApp || false,
        }

        let appRef = await db.collection("apps").add(newAppData);

        for (let attempts = 0; attempts < 10; attempts++) {
            if (appRef.id in store.getState().data.apps) break;
            await delay(500);
        }

        logAnalyticsEvent("apk_upload", { packageName: newAppData.packageName, title: newAppData.title });
        history.push(createInfoUrl(MANAGED_APPS, appRef.id));
    } catch (error) {
        dispatch({
            type: SET_ERRORS,
            payload: error.message
        });
    }

    dispatch({
        type: UPLOADING,
        payload: { status: false }
    });
    dispatch({ type: LOADING_UI, payload: false });
}

export const editManagedApp = ({ key, title, packageName, description = "", vrTypes = [], categories = [], icon, oldIconUrl, organizationKey, globalAccess, sortPriority }) => dispatch => {
    dispatch({ type: LOADING_UI, payload: true });
    dispatch({
        type: UPLOADING,
        payload: { status: true, title: "Managed App: " + title }
    });

    var promises = [];
    if (icon)
        promises.push(uploadFile(getIconStoragePath(packageName), icon, dispatch));
    Promise.all(promises).then(urls => {
        dispatch({
            type: UPLOADING,
            payload: { status: false }
        });
        const iconUrl = icon ? urls[0] : oldIconUrl;
        db.collection("apps")
            .doc(key)
            .update({
                title,
                description,
                vrTypes,
                categories,
                iconUrl,
                organizationRef: getOrganizationRef(organizationKey),
                globalAccess,
                sortPriority
            });
    });
};
export const deleteApp = appKey => dispatch => {
    dispatch({ type: LOADING_UI, payload: true });
    // TODO move the handling to backend
    mapKV(store.getState().data.contentBundles, (bundleKey, { appRefs }) => {
        if (appRefs.find(s => s.id == appKey)) {
            db.collection("contentBundles")
                .doc(bundleKey)
                .update({ appRefs: appRefs.filter(s => s.id !== appKey) });
        }
    });
    mapKV(store.getState().data.deviceGroups, (deviceGroupsKey, { approvedApps }) => {
        if (!approvedApps) return;
        if (approvedApps.find(s => s.id == appKey)) {
            db.collection("deviceGroups")
                .doc(deviceGroupsKey)
                .update({ approvedApps: approvedApps.filter(s => s.id !== appKey) });
        }
    });
    db.collection("apps")
        .doc(appKey)
        .delete()
        .finally(() => {
            dispatch({ type: LOADING_UI, payload: false });
        });
};

export const makeAppDefaultVersion = (appKey, dispatch) => {
    dispatch({ type: LOADING_UI, payload: true });

    db.collection("apps")
        .doc(appKey)
        .update({ defaultVersion: true });
};